<template>
    <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
        <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex min-w-0">
                    <svg-vue class="content-title-icon" icon="font-awesome.edit-regular"></svg-vue>
                    <h1 class="content-title">
                        {{ $t('Product detail') }}
                    </h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <loading :status="loading.main"></loading>
                    <button @click="printProduct" class="btn btn-app rounded-none rounded-l-md" type="button">
                        {{ $t('Print') }}
                    </button>
                    <template v-if="product.has_variants">
                        <download-csv class="btn btn-app rounded-none" :data="product.variants">
                            {{ $t('Export variants') }}
                        </download-csv>
                    </template>
                    <template v-else>
                        <router-link :to="`/print/product-label/${product.upc}/${product.barcode_symbology}/${product.name}/${product.price}/${product.identity}`" class="btn btn-app rounded-none" type="button">
                            {{ $t('Label') }}
                        </router-link>
                    </template>
                    <button @click="deleteProductModal = true" class="btn btn-app rounded-none" type="button">
                        {{ $t('Delete') }}
                    </button>
                    <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin/products">
                        {{ $t('Back') }}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
            <div class="content-card-body">
                <div class="w-full px-5">
                    <ul class="flex mb-0 list-none flex-wrap pt-3 pb-2 flex-row">
                        <li class="mb-1 last:mr-0 flex-auto text-center">
                            <a
                                class="cursor-pointer font-bold px-4 py-2 border block leading-normal rounded-l-md"
                                @click="toggleTabs(1)"
                                :class="{
                                    'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 1,
                                    'text-white bg-green-500': openTab === 1,
                                }"
                            >
                                {{ $t('Information') }}
                            </a>
                        </li>
                        <li class="mb-1 last:mr-0 flex-auto text-center">
                            <a
                                class="cursor-pointer font-bold px-4 py-2 border block leading-normal"
                                @click="toggleTabs(2)"
                                :class="{
                                    'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 2,
                                    'text-white bg-green-500': openTab === 2,
                                }"
                            >
                                {{ $t('Edit') }}
                            </a>
                        </li>

                        <template v-if="product.has_variants">
                            <li class="mb-1 last:mr-0 flex-auto text-center">
                                <a
                                    class="cursor-pointer font-bold px-4 py-2 border block leading-normal"
                                    @click="toggleTabs(3)"
                                    :class="{
                                        'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 3,
                                        'text-white bg-green-500': openTab === 3,
                                    }"
                                >
                                    {{ $t('Variants List') }}
                                </a>
                            </li>
                            <li class="flex-auto text-center">
                                <a
                                    class="cursor-pointer font-bold px-4 py-2 border block leading-normal"
                                    @click="toggleTabs(4)"
                                    :class="{
                                        'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 4,
                                        'text-white bg-green-500': openTab === 4,
                                    }"
                                >
                                    <template v-if="productAttribute.is_edit">
                                        {{ $t('Update Variant') }}
                                    </template>
                                    <template v-else>
                                        {{ $t('Add Variant') }}
                                    </template>
                                </a>
                            </li>
                            <li class="last:mr-0 flex-auto text-center">
                                <a
                                    class="cursor-pointer font-bold px-4 py-2 border block leading-normal rounded-r-md"
                                    @click="toggleTabs(5)"
                                    :class="{
                                        'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 5,
                                        'text-white bg-green-500': openTab === 5,
                                    }"
                                >
                                    {{ $t('Import variant') }}
                                </a>
                            </li>
                        </template>
                        <template v-else>
                            <li class="mb-1 last:mr-0 flex-auto text-center">
                                <a
                                    class="cursor-pointer font-bold px-4 py-2 border block leading-normal rounded-r-md"
                                    @click="toggleTabs(6)"
                                    :class="{
                                        'text-gray-600 bg-green-100 hover:bg-green-200': openTab !== 6,
                                        'text-white bg-green-500': openTab === 6,
                                    }"
                                >
                                    {{ $t('Stocks') }}
                                </a>
                            </li>
                        </template>
                    </ul>
                </div>
                <div class="relative flex flex-col min-w-0 break-words w-full">
                    <div class="flex-auto">
                        <loading :status="loading.main"></loading>
                        <div class="tab-content tab-space">
                            <!-- information tab -->
                            <div id="printProduct" :class="{ hidden: openTab !== 1, block: openTab === 1 }">
                                <div class="px-4 py-5 sm:px-6 rounded-md">
                                    <img :src="image.preview" class="rounded-md mb-3 w-24 h-24" />
                                    <h3 class="text-lg leading-6 font-medium text-gray-900">
                                        {{ product.name | capitalize }}
                                    </h3>
                                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                                        {{ product.created_at }}
                                    </p>
                                </div>
                                <div class="border-t border-gray-200">
                                    <dl>
                                        <div v-if="product.category" class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">
                                                {{ $t('Category') }}
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                {{ product.category.name }}
                                            </dd>
                                        </div>

                                        <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">
                                                {{ $t('Status') }}
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                <div class="mt-2 flex items-center text-sm leading-5 text-gray-900" v-show="product.status">
                                                    <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-green-400" icon="font-awesome.check-circle-solid"></svg-vue>
                                                    {{ $t('Activated') }}
                                                </div>
                                                <div class="mt-2 flex items-center text-sm leading-5 text-red-500" v-show="!product.status">
                                                    <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-red-400" icon="font-awesome.times-circle-solid"></svg-vue>
                                                    {{ $t('Disabled') }}
                                                </div>
                                            </dd>
                                        </div>
                                        <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">
                                                {{ $t('Barcode symbology') }}
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                {{ product.barcode_symbology }}
                                            </dd>
                                        </div>
                                        <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">
                                                {{ $t('Quantity low alert at') }}
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                {{ product.low_qty_alert }}
                                            </dd>
                                        </div>

                                        <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">
                                                {{ $t('Description') }}
                                            </dt>
                                            <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                {{ $t(product.description) }}
                                            </dd>
                                        </div>
                                        <template v-if="product.has_variants">
                                            <div class="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">
                                                    {{ $t('Variants') }}
                                                </dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                    <ul role="list" class="border border-gray-200 rounded-md divide-y divide-gray-200">
                                                        <template v-for="(item, index) in product.variants">
                                                            <li
                                                                @click="editProductAttibute(item)"
                                                                class="pl-3 pr-4 py-3 flex items-center justify-between text-sm cursor-pointer hover:border-blue-200"
                                                            >
                                                                <div class="w-0 flex-1 flex items-center">
                                                                    <span class="ml-2 flex-1 w-0 truncate">
                                                                        <strong>{{ item.title | capitalize }}</strong>
                                                                        <barcode
                                                                            height="20"
                                                                            margin-left="0"
                                                                            font-size="15"
                                                                            :format="product.barcode_symbology"
                                                                            :text="item.upc"
                                                                            :value="item.upc"
                                                                        >
                                                                        </barcode>
                                                                    </span>
                                                                </div>
                                                                <div class="ml-4 flex-shrink-0">
                                                                    {{ item.price | currency }}
                                                                </div>
                                                            </li>
                                                        </template>
                                                    </ul>
                                                </dd>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">
                                                    {{ $t('Stock keeping unit') }}
                                                </dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                    {{ $t(product.sku) }}
                                                </dd>
                                            </div>
                                            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">
                                                    {{ $t('Universal product code') }}
                                                </dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                    <barcode
                                                        :value="product.upc"
                                                        height="20"
                                                        margin-left="0"
                                                        :text="product.upc"
                                                        font-size="15"
                                                        :format="product.barcode_symbology"
                                                    >
                                                    </barcode>
                                                </dd>
                                            </div>
                                            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">
                                                    {{ $t('Serial number') }}
                                                </dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                    {{ $t(product.serial_number) }}
                                                </dd>
                                            </div>
                                            <div class="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">
                                                    {{ $t('Identity') }}
                                                </dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                    {{ $t(product.identity) }}
                                                </dd>
                                            </div>
                                        </template>
                                    </dl>
                                </div>
                            </div>
                            <!-- end -->

                            <!-- editing -->
                            <div :class="{ hidden: openTab !== 2, block: openTab === 2 }">
                                <form @submit.prevent="updateProduct">
                                    <loading :status="loading.edit"></loading>
                                    <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                                        <div class="md:col-span-1">
                                            <h3 class="form-description-head">
                                                {{ $t('Product details') }}
                                            </h3>
                                            <p class="form-description-text">{{ $t('This information will be displayed publicly') }}.</p>
                                        </div>
                                        <div class="mt-5 md:mt-0 md:col-span-2">
                                            <div class="grid grid-cols-3 gap-6">
                                                <div class="col-span-3">
                                                    <label class="form-label" for="name">{{ $t('Name') }}</label>
                                                    <div class="pt-1">
                                                        <input :placeholder="$t('Name')" class="form-input" id="name" required v-model="product.name" />
                                                    </div>
                                                </div>
                                                <div class="col-span-3 md:flex md:space-x-2">
                                                    <div class="w-full">
                                                        <label class="form-label" for="category">{{ $t('Category') }}</label>
                                                        <div class="pt-1">
                                                            <select class="form-input bg-white" id="category" required v-model="product.category_id">
                                                                <option :value="null" disabled>
                                                                    {{ $t('Select an option') }}
                                                                </option>
                                                                <option :key="index" :value="category.id" v-for="(category, index) in productCategories">
                                                                    {{ category.name }}
                                                                </option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div class="w-full">
                                                        <label class="form-label" for="supplier">{{ $t('Supplier') }}</label>
                                                        <div class="pt-1">
                                                            <select class="form-input bg-white" id="supplier" v-model="product.supplier_id">
                                                                <option :value="null" disabled>
                                                                    {{ $t('Select an option') }}
                                                                </option>
                                                                <option :key="index" :value="supplier.id" v-for="(supplier, index) in productSuppliers">
                                                                    {{ supplier.first_name }} {{ supplier.last_name }}
                                                                </option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="col-span-3">
                                                    <label class="form-label" for="description">{{ $t('Description') }}</label>
                                                    <div class="pt-1">
                                                        <textarea
                                                            rows="2"
                                                            :placeholder="$t('Description')"
                                                            class="form-input"
                                                            id="description"
                                                            v-model="product.description"
                                                        ></textarea>
                                                    </div>
                                                </div>
                                                <div class="col-span-3 md:flex md:space-x-2">
                                                    <div class="w-full">
                                                        <label class="form-label" for="low_qty_alert">
                                                            {{ $t('Quantity low alert at') }}
                                                        </label>
                                                        <div class="pt-1">
                                                            <input
                                                                :placeholder="$t('Quantity low alert at')"
                                                                class="form-input"
                                                                id="low_qty_alert"
                                                                required
                                                                type="number"
                                                                min="1"
                                                                v-model="product.low_qty_alert"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="w-full">
                                                        <label class="form-label" for="barcode_symbology">{{ $t('Barcode symbology') }}</label>
                                                        <div class="pt-1">
                                                            <select class="form-input bg-white" id="barcode_symbology" required v-model="product.barcode_symbology">
                                                                <option :value="null" disabled>
                                                                    {{ $t('Select an option') }}
                                                                </option>
                                                                <option value="CODE128">CODE128</option>
                                                                <option value="CODE39">CODE39</option>
                                                                <option value="EAN13">EAN13</option>
                                                                <option value="MSI">MSI</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <div class="py-3">
                                                <div class="border-t border-gray-200"></div>
                                            </div>
                                        </div>
                                        <div class="md:col-span-1">
                                            <h3 class="form-description-head">
                                                {{ $t('Product controls') }}
                                            </h3>
                                            <p class="form-description-text">{{ $t('Product listing control and other things') }}.</p>
                                        </div>
                                        <div class="mt-5 md:mt-0 md:col-span-2">
                                            <div class="grid grid-cols-3 gap-6">
                                                <div class="col-span-3">
                                                    <label class="form-label" for="status">{{ $t('Status') }}</label>
                                                    <div class="mt-1 flex rounded-md">
                                                        <input-switch
                                                            :disabled-label="$t('The product is disabled')"
                                                            :enabled-label="$t('The product is activated')"
                                                            id="status"
                                                            v-model="product.status"
                                                        ></input-switch>
                                                    </div>
                                                    <div class="form-description-text">
                                                        {{ $t('When the product is deactivated,it will not be available for sale') }}
                                                    </div>
                                                </div>

                                                <div class="col-span-3">
                                                    <label class="form-label" for="has_variants">{{ $t('Attributes') }}</label>
                                                    <div class="mt-1 flex rounded-md">
                                                        <input-switch
                                                            :disabled-label="$t('The product have no variant')"
                                                            :enabled-label="$t('The product has multiple variants')"
                                                            id="has_variants"
                                                            v-model="product.has_variants"
                                                        ></input-switch>
                                                    </div>
                                                    <div class="form-description-text">
                                                        {{
                                                            ($t('When the product has multiple variants'),
                                                            $t('each variant can be have separate sale price'),
                                                            $t('purchase cost and available quantity control'))
                                                        }}
                                                    </div>
                                                </div>
                                                 <div class="col-span-3">
                                                    <label class="form-label" for="is_serial_based">{{ $t('Is serial based') }}</label>
                                                    <div class="mt-1 flex rounded-md">
                                                        <input-switch
                                                            :disabled-label="$t('None serial based')"
                                                            :enabled-label="$t('Serial based')"
                                                            id="is_serial_based"
                                                            v-model="product.is_serial_based"
                                                        ></input-switch>
                                                    </div>
                                                </div>
                                                <template v-if="!product.has_variants">
                                                    <div class="col-span-3 md:flex md:space-x-2">
                                                        <div class="w-full">
                                                            <label class="form-label" for="cost">
                                                                {{ $t('Unit cost') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input
                                                                    :placeholder="$t('Unit cost')"
                                                                    type="number"
                                                                    step="0.01"
                                                                    class="form-input"
                                                                    id="cost"
                                                                    v-model="product.cost"
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div class="w-full">
                                                            <label class="form-label" for="price">
                                                                {{ $t('Unit Price') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input
                                                                    :placeholder="$t('Unit Price')"
                                                                    type="number"
                                                                    step="0.01"
                                                                    class="form-input"
                                                                    id="price"
                                                                    v-model="product.price"
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div class="col-span-3 md:flex md:space-x-2">
                                                        <div class="w-full">
                                                            <label class="form-label" for="upc">
                                                                {{ $t('Barcode') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input :placeholder="$t('Universal product code')" class="form-input" id="upc" v-model="product.upc" />
                                                            </div>
                                                        </div>
                                                        <div class="w-full">
                                                            <label class="form-label" for="sku">
                                                                {{ $t('SKU') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input :placeholder="$t('Stock keeping unit')" class="form-input" id="sku" v-model="product.sku" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-if="product.is_serial_based" class="col-span-3 md:flex md:space-x-2">
                                                        <div class="w-full">
                                                            <label class="form-label" for="serial_number">
                                                                {{ $t('Serial number') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input
                                                                    :placeholder="$t('Serial number')"
                                                                    class="form-input"
                                                                    id="serial_number"
                                                                    v-model="product.serial_number"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div class="w-full">
                                                            <label class="form-label" for="identity">
                                                                {{ $t('Identity') }}
                                                            </label>
                                                            <div class="pt-1">
                                                                <input :placeholder="$t('Identity')" class="form-input" id="identity" v-model="product.identity" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                        <div class="md:col-span-3">
                                            <div class="py-2">
                                                <div class="border-t border-gray-200"></div>
                                            </div>
                                        </div>
                                        <div class="md:col-span-1">
                                            <h3 class="form-description-head">
                                                {{ $t('Product Image') }}
                                            </h3>
                                            <p class="form-description-text">{{ $t('Upload product image') }}.</p>
                                        </div>
                                        <div class="md:mt-0 md:col-span-2">
                                            <div class="grid grid-cols-3 gap-6">
                                                <div class="col-span-3">
                                                    <loading :status="loading.image"></loading>
                                                    <image-input :remove="false" id="avator" v-model="image"></image-input>
                                                    <br />
                                                    <button type="button" class="btn btn-app" :disabled="!image.file" @click.prevent="updateProductImage">
                                                        {{ $t('Upload') }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-footer">
                                        <span class="inline-flex">
                                            <router-link class="btn btn-app shadow-sm rounded-md mr-4" to="/admin/products">
                                                {{ $t('Close') }}
                                            </router-link>
                                            <button class="btn btn-app shadow-sm rounded-md" type="submit">
                                                {{ $t('Update') }}
                                            </button>
                                        </span>
                                    </div>
                                </form>
                            </div>
                            <!-- end -->
                            <template v-if="product.has_variants">
                                <!-- Stock listing -->
                                <div :class="{ hidden: openTab !== 3, block: openTab === 3 }">
                                    <div class="overflow-hidden border-b border-green-200 px-6 pb-6">
                                        <div class="flex flex-col">
                                            <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div class="-my-2 rounded-md overflow-x-auto sm:-mx-6 lg:-mx-8">
                                                    <loading :status="loading.attribute"></loading>
                                                    <table class="min-w-full divide-y divide-green-200 text-left">
                                                        <thead class="bg-green-500">
                                                            <tr>
                                                                <th scope="col" class="w-1/3 py-2 pl-2 font-medium text-white uppercase">
                                                                    {{ $t('Name') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('Barcode') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('SKU') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('Cost') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('Price') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('SN') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('Identity') }}
                                                                </th>
                                                                <th scope="col" class="px-2 py-2 font-medium text-white uppercase tracking-wider">
                                                                    {{ $t('Action') }}
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody class="bg-white divide-y divide-gray-200">
                                                            <tr v-for="(variant, index) in product.variants" :key="index" class="taxt-xs hover:bg-gray-100">
                                                                <td class="py-2 pl-2 break-normal md:break-all">
                                                                    <div class="text-gray-500">
                                                                        {{ variant.title }}
                                                                    </div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">{{ variant.upc }}</div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">{{ variant.sku }}</div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">
                                                                        {{ variant.cost | currency }}
                                                                    </div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">
                                                                        {{ variant.price | currency }}
                                                                    </div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">
                                                                        {{ variant.serial_number }}
                                                                    </div>
                                                                </td>
                                                                <td class="px-2 py-2 whitespace-nowrap">
                                                                    <div class="text-gray-500">
                                                                        {{ variant.identity }}
                                                                    </div>
                                                                </td>

                                                                <td class="px-2 py-2 flex">
                                                                    <button
                                                                        @click.prevent="showStocks(variant)"
                                                                        class="text-gray-500 bg-green-100 rounded-none px-2 py-1 rounded-l-md text-xs"
                                                                    >
                                                                        {{ $t('Stocks') }}
                                                                    </button>
                                                                    <router-link
                                                                        class="text-gray-500 bg-green-100 px-2 py-1 rounded-none text-xs"
                                                                        :to="`/print/product-label/${variant.upc}/${product.barcode_symbology}/${variant.title}/${variant.price}/${variant.identity}`"
                                                                    >
                                                                        {{ $t('Label') }}
                                                                    </router-link>
                                                                    <button
                                                                        @click="editProductAttibute(variant)"
                                                                        class="text-gray-500 bg-green-100 px-2 py-1 rounded-none text-xs"
                                                                    >
                                                                        {{ $t('Edit') }}
                                                                    </button>
                                                                    <button
                                                                        class="text-gray-500 bg-green-100 px-2 py-1 rounded-none text-xs rounded-r-md"
                                                                        @click="attributeDelete(variant.id)"
                                                                    >
                                                                        {{ $t('Delete') }}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- end -->
                                <!-- Stock importing -->
                                <div :class="{ hidden: openTab !== 4, block: openTab === 4 }">
                                    <form @submit.prevent="submitProductAttribute">
                                        <loading :status="loading.attribute"></loading>
                                        <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                                            <div class="md:col-span-1">
                                                <h3 class="form-description-head">
                                                    {{ $t('Product variant') }}
                                                </h3>
                                                <p class="form-description-text">{{ $t('This is information about product variant') }}.</p>
                                            </div>
                                            <div class="mt-5 md:mt-0 md:col-span-2">
                                                <div class="grid grid-cols-3 gap-6">
                                                    <div class="col-span-3">
                                                        <label class="form-label" for="title">{{ $t('Title') }}</label>
                                                        <div class="pt-1">
                                                            <input :placeholder="$t('Title')" class="form-input" id="title" v-model="productAttribute.title" required />
                                                        </div>
                                                    </div>
                                                    <div class="col-span-3 md:flex md:space-x-2">

                                                    <div class="w-full">
                                                        <label class="form-label" for="Upc">
                                                            {{ $t('Barcode') }}
                                                        </label>
                                                        <div class="pt-1">
                                                            <input
                                                                :placeholder="$t('Universal product code')"
                                                                class="form-input"
                                                                id="Upc"
                                                                v-model="productAttribute.upc"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div class="w-full">
                                                        <label class="form-label" for="sku">{{ $t('SKU') }}</label>
                                                        <div class="pt-1">
                                                            <input :placeholder="$t('Stock keeping unit')" class="form-input" id="sku" v-model="productAttribute.sku" />
                                                        </div>
                                                    </div>
                                                </div>
                                                    <div class="col-span-3 md:flex md:space-x-2">
                                                    <div class="w-full">
                                                        <label class="form-label" for="cost">{{ $t('Cost') }}</label>
                                                        <div class="pt-1">
                                                            <input :placeholder="$t('Cost')" class="form-input" id="cost" type="number" v-model="productAttribute.cost" />
                                                        </div>
                                                    </div>
                                                    <div class="w-full">
                                                        <label class="form-label" for="price">{{ $t('Price') }}</label>
                                                        <div class="pt-1">
                                                            <input
                                                                :placeholder="$t('Price')"
                                                                class="form-input"
                                                                id="price"
                                                                type="number"
                                                                v-model="productAttribute.price"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                    <div class="col-span-3">
                                                        <label class="form-label" for="is_serial_based">{{ $t('Is serial based') }}</label>
                                                        <input-switch
                                                            :disabled-label="$t('The product attribute is serial based')"
                                                            :enabled-label="$t('The product attribute is not serial based')"
                                                            id="is_serial_based"
                                                            v-model="productAttribute.is_serial_based"
                                                        ></input-switch>
                                                        <div class="form-description-text">{{ $t('This option controls whether product is serial based') }}.</div>
                                                    </div>
                                                    <template v-if="productAttribute.is_serial_based">
                                                <div class="col-span-3 md:flex md:space-x-2">
                                                        <div class="w-full">
                                                            <label class="form-label" for="identity">{{ $t('Identity') }}</label>
                                                            <div class="pt-1">
                                                                <input
                                                                    :placeholder="$t('Identity')"
                                                                    class="form-input"
                                                                    id="identity"
                                                                    type="identity"
                                                                    v-model="productAttribute.identity"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div class="w-full">
                                                            <label class="form-label" for="serial_number">{{ $t('Serial number') }}</label>
                                                            <div class="pt-1">
                                                                <input
                                                                    :placeholder="$t('Serial number')"
                                                                    class="form-input"
                                                                    id="serial_number"
                                                                    type="serial_number"
                                                                    v-model="productAttribute.serial_number"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="card-footer">
                                            <span class="inline-flex">
                                                <button class="btn btn-app shadow-sm rounded-md" type="submit">
                                                    {{ $t('Save') }}
                                                </button>
                                            </span>
                                        </div>
                                    </form>
                                </div>
                                <!-- end -->

                                <!-- import variant -->
                                <div :class="{ hidden: openTab !== 5, block: openTab === 5 }">
                                    <div class="md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                                        <div class="md:col-span-1">
                                            <h3 class="form-description-head">
                                                {{ $t('Batch entries with CSV import') }}
                                            </h3>
                                            <p class="form-description-text">
                                                {{ $t('Upload file for batch entries') }}.
                                                <br />
                                                {{ $t('A file should be CSV with format') }}.
                                                <br />
                                                {{ $t('A file must have columns with title of') }}
                                                <br />
                                                <span class="text-red-600">"title"</span>
                                                <span class="text-red-600">"sku"</span>
                                                <span class="text-red-600">"upc"</span>
                                                <span class="text-red-600">"cost"</span>
                                                <span class="text-red-600">"price"</span>
                                                <span class="text-red-600">"ws_price"</span>
                                                <span class="text-red-600">"is_serial_based"</span>
                                                <span class="text-red-600">"serial_number"</span>
                                                <span class="text-red-600">"identity"</span>
                                            </p>
                                        </div>
                                        <div class="md:mt-0 md:col-span-2">
                                            <div class="grid grid-cols-3 gap-6">
                                                <div class="col-span-3">
                                                    <label class="form-label" for="name">{{ $t('Excel CSV file') }}</label>
                                                    <div class="pt-1">
                                                        <input @change="onChangeFileUpload" class="form-input" ref="file" required type="file" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-footer">
                                        <span class="inline-flex">
                                            <button :disabled="!file" @click="importProductAttributes" class="btn btn-app" type="button">
                                                {{ $t('Upload') }}
                                            </button>
                                        </span>
                                    </div>
                                </div>
                                <!-- end -->
                            </template>
                            <template v-else>
                                <div :class="{ hidden: openTab !== 6, block: openTab === 6 }">
                                    <div class="rounded-md relative flex-1 px-4 sm:px-6 h-full overflow-y-scroll">
                                        <table class="w-full">
                                            <thead>
                                                <tr class="text-xs text-left border-b border-gray-700 text-gray-600 bg-gray-100">
                                                    <th class="p-2">{{ $t('Added at') }}</th>
                                                    <th class="p-2">{{ $t('Store in warehouse') }}</th>
                                                    <th class="p-2">{{ $t('At rack') }}</th>
                                                    <th class="p-2">{{ $t('Sold quantity') }}</th>
                                                    <th class="p-2">{{ $t('Available quantity') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr class="text-left text-xs text-gray-500 border-b hover:bg-gray-100" v-for="(stock, index) in stocks" :key="index">
                                                    <td class="p-2">{{ stock.created_at }}</td>
                                                    <td class="p-2">{{ stock.warehouse.name }}</td>
                                                    <td class="p-2">{{ stock.rack_number }}</td>
                                                    <td class="p-2">{{ stock.sold }}</td>
                                                    <td class="p-2">
                                                        <a target="_blank" class="text-blue-500 hover:text-blue-600" :href="`/admin/stocks/${stock.uuid}/edit`">
                                                            {{ stock.quantity }}
                                                        </a>
                                                    </td>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                                <tr class="text-gray-500">
                                                    <td>
                                                        <router-link class="text-blue-400 hover:text-blue-600 text-xs" :to="addStockLink">
                                                            {{ $t('Add stock quantity') }}
                                                        </router-link>
                                                    </td>
                                                    <td colspan="3" class="px-2 text-right">
                                                        {{ $t('Total in stock') }}
                                                    </td>
                                                    <td class="px-2">{{ totalInStock }}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <product-delete-model :show="deleteProductModal" @cancelled="deleteProductModal = false" @confirmed="deleteProduct">
            <template #title>{{ $t('Delete product') }}</template>
            {{ $t('Are you sure you want to delete the product ?') }}
            {{ $t('All data will be permanently removed') }}.
        </product-delete-model>
        <product-attribute-delete-model :show="deleteProductAttributeModal" @cancelled="deleteProductAttributeModal = false" @confirmed="deleteProductAttribute">
            <template #title>{{ $t('Delete product attribute') }}</template>
            {{ $t('Are you sure you want to delete the product attribute?') }}
            {{ $t('All data will be permanently removed') }}.
        </product-attribute-delete-model>
        <!-- side filters -->
        <div class="fixed inset-0 overflow-hidden z-30" v-if="showSidebar">
            <div class="absolute inset-0 overflow-hidden">
                <section class="absolute inset-y-0 right-0 pl-10 max-w-full flex">
                    <transition
                        duration="500"
                        enter-active-class="transform transition ease-in-out duration-500"
                        enter-class="translate-x-full"
                        enter-to-class="translate-x-0"
                        leave-active-class="transform transition ease-in-out duration-500"
                        leave-class="translate-x-0"
                        leave-to-class="translate-x-full"
                    >
                        <div class="w-screen max-w-xl" v-if="showSidebar">
                            <div class="h-full flex flex-col space-y-4 bg-white shadow-xl">
                                <header class="px-4 pt-4 sm:px-6">
                                    <div class="flex items-start justify-between space-x-3">
                                        <h2 class="text-lg leading-7 font-medium text-gray-900">{{ $t('Stock list') }} ({{ stocks.length }})</h2>

                                        <div class="h-7 flex items-center">
                                            <button
                                                @click.prevent="closeSidebar"
                                                aria-label="Close panel"
                                                class="text-gray-400 hover:text-gray-900 transition ease-in-out duration-150"
                                                type="button"
                                            >
                                                <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </header>

                                <div class="relative flex-1 px-4 sm:px-6 h-full border-t overflow-y-scroll">
                                    <div class="absolute inset-0 px-4 sm:px-6">
                                        <table class="w-full my-4">
                                            <thead class="mb-1">
                                                <tr class="rounded-md text-xs text-left border-b border-gray-700 text-gray-600 bg-gray-100">
                                                    <th class="px-2 py-1">{{ $t('Added at') }}</th>
                                                    <th class="px-2 py-1">{{ $t('Store in warehouse') }}</th>
                                                    <th class="px-2 py-1">{{ $t('At rack') }}</th>
                                                    <th class="px-2 py-1">{{ $t('Sold quantity') }}</th>
                                                    <th class="px-2 py-1">{{ $t('Returned quantity') }}</th>
                                                    <th class="px-2 py-1">{{ $t('Available quantity') }}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr class="text-left text-xs text-gray-500 border-b hover:bg-gray-100" v-for="(stock, index) in stocks" :key="index">
                                                    <td class="px-2">{{ stock.created_at }}</td>
                                                    <td class="px-2">{{ stock.warehouse.name }}</td>
                                                    <td class="px-2">{{ stock.rack_number }}</td>
                                                    <td class="px-2">{{ stock.sold }}</td>
                                                    <td class="px-2">{{ stock.returned }}</td>
                                                    <td class="px-2">
                                                        <a target="_blank" class="text-blue-500 hover:text-blue-600" :href="`/admin/stocks/${stock.uuid}/edit`">
                                                            {{ stock.quantity }}
                                                        </a>
                                                    </td>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                                <tr class="text-gray-500">
                                                    <td>
                                                        <router-link class="text-blue-400 hover:text-blue-600 text-xs" :to="addStockLink">
                                                            {{ $t('Add stock quantity') }}
                                                        </router-link>
                                                    </td>
                                                    <td colspan="4" class="px-2 text-right">
                                                        {{ $t('Total in stock') }}
                                                    </td>
                                                    <td class="px-2">{{ totalInStock }}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </section>
            </div>
        </div>
        <!-- end -->
    </main>
</template>
<script>
import confirmationModel from '@/components/layout/admin/confirmation';
import VueBarcode from 'vue-barcode';
export default {
    name: 'edit',
    metaInfo() {
        return {
            title: this.$i18n.t('Edit product'),
        };
    },
    components: {
        productDeleteModel: confirmationModel,
        productAttributeDeleteModel: confirmationModel,
        barcode: VueBarcode,
    },
    data() {
        return {
            loading: {
                main: true,
                attribute: false,
                image: false,
                edit: false,
                import: false,
            },
            currentVariant: {},
            showSidebar: false,
            file: null,
            openTab: 1,
            deleteProductModal: false,
            deleteProductAttributeModal: false,
            productCategories: [],
            productSuppliers: [],
            image: { file: null, preview: null },
            attribute_id: null,
            stocks: [],
            product: {
                name: null,
                low_qty_alert: 5,
                category_id: null,
                supplier_id: null,
                description: null,
                variants: [],
                status: false,
                has_variants: false,
                serial_number: null,
                sku: null,
                upc: null,
                identity: null,
                barcode_symbology: 'CODE128',
            },
            productAttribute: {
                is_edit: false,
                is_serial_based: false,
                title: null,
                cost: 0,
                price: 0,
                ws_price: 0,
                sku: null,
                upc: null,
                serial_number: null,
            },
        };
    },
    mounted() {
        this.getProductSuppliers();
        this.getProductCategories();
    },
    computed: {
        totalInStock() {
            let items = 0;
            this.stocks.forEach((stock) => {
                items += Number(stock.quantity);
            });
            return items;
        },
        addStockLink() {
            if (this.product.has_variants) {
                return `/admin/stocks/new?product_id=${this.product.id}&varient_id=${this.currentVariant.id}&open_tab=3&stock_list_id=${this.currentVariant.id}&return_url=/admin/products/${this.product.id}/edit`;
            } else {
                return `/admin/stocks/new?product_id=${this.product.id}&return_url=/admin/products/${this.product.id}/edit&open_tab=6`;
            }
        },
    },
    methods: {
        closeSidebar() {
            this.stocks = [];
            this.showSidebar = false;
        },
        showStocks(variant) {
            this.currentVariant = variant;
            this.stocks = variant.stocks;
            this.showSidebar = true;
        },
        async printProduct() {
            await this.$htmlToPaper('printProduct');
        },
        importProductAttributes() {
            this.loading.import = true;
            const formData = new FormData();
            formData.append('file', this.file);
            formData.append('product.id', this.product.id);
            axios
                .post(route('product-attributes.import', this.product.id), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                .then((response) => {
                    this.loading.import = false;
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                    this.getProduct();
                    this.openTab = 2;
                })
                .catch(() => {
                    this.loading.import = false;
                });
        },
        onChangeFileUpload() {
            this.file = this.$refs.file.files[0];
        },
        attributeDelete(id) {
            this.deleteProductAttributeModal = true;
            this.attribute_id = id;
        },
        editProductAttibute(item) {
            this.productAttribute = item;
            this.productAttribute.is_edit = true;
            this.openTab = 4;
        },
        submitProductAttribute() {
            if (this.productAttribute.is_edit) {
                this.updateProductAttribute();
            } else {
                this.saveProductAttribute();
            }
        },
        updateProductAttribute() {
            this.productAttribute.product_id = this.product.id;
            this.loading.attribute = true;
            axios
                .patch(route('product-attributes.update', this.productAttribute.id), this.productAttribute)
                .then((response) => {
                    this.loading.attribute = false;
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                    Object.keys(this.productAttribute).forEach((i) => (this.productAttribute[i] = null));
                    this.getProduct();
                    this.openTab = 3;
                })
                .catch(() => {
                    this.loading.attribute = false;
                });
        },
        saveProductAttribute() {
            this.loading.attribute = true;
            this.productAttribute.product_id = this.product.id;
            axios
                .post(route('product-attributes.store'), this.productAttribute)
                .then((response) => {
                    this.loading.attribute = false;
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                    this.product.variants.push(response.data.attribute);
                    Object.keys(this.productAttribute).forEach((i) => (this.productAttribute[i] = null));
                    this.openTab = 3;
                })
                .catch(() => {
                    this.loading.attribute = false;
                });
        },
        toggleTabs(tabNumber) {
            this.openTab = tabNumber;
        },
        updateProductImage() {
            this.loading.image = true;
            const formData = new FormData();
            formData.append('product_image', this.image.file);
            axios
                .post(route('products.image', this.$route.params.id), formData)
                .then((response) => {
                    this.loading.image = false;
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                })
                .catch(() => {
                    this.loading.image = false;
                });
        },
        updateProduct() {
            this.loading.edit = true;
            axios
                .patch(route('products.update', this.$route.params.id), this.product)
                .then((response) => {
                    this.loading.edit = false;
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                })
                .catch(() => {
                    this.loading.edit = false;
                });
        },
        getProduct() {
            this.loading.main = true;
            axios
                .get(route('products.show', this.$route.params.id))
                .then((response) => {
                    this.product = response.data;
                    this.product.category_id = response.data.category.id;
                    this.image.preview = response.data.avatar;
                    this.loading.main = false;
                    this.loading.attribute = false;
                    this.stocks = this.product.stocks;
                    this.productAttribute.cost = this.product.cost;
                    this.productAttribute.price = this.product.price;
                    if (this.$route.query.open_tab !== undefined) {
                        this.openTab = Number(this.$route.query.open_tab) ?? 1;
                        const variant = this.product.variants.find((item) => item.id === Number(this.$route.query.stock_list_id));
                        this.showStocks(variant);
                    }
                })
                .catch(() => {
                    this.loading.main = false;
                });
        },
        deleteProductAttribute() {
            this.loading.attribute = true;
            axios
                .delete(route('product-attributes.destroy', this.attribute_id))
                .then((response) => {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                    this.deleteProductAttributeModal = false;
                    this.loading.attribute = true;
                    this.getProduct();
                })
                .catch(() => {
                    this.loading.attribute = false;
                    this.deleteProductAttributeModal = false;
                });
        },
        deleteProduct() {
            this.deleteProductModal = false;
            this.loading.main = true;
            axios
                .delete(route('products.destroy', this.$route.params.id))
                .then((response) => {
                    this.$notify({
                        title: this.$i18n.t('Success').toString(),
                        text: response.data.message.toString(),
                        type: 'success',
                    });
                    this.$router.push('/admin/products');
                })
                .catch(() => {
                    this.deleteProductModal = false;
                    this.loading.main = false;
                });
        },
        getProductSuppliers() {
            this.loading.main = true;
            axios
                .get(route('product.suppliers'))
                .then((response) => {
                    this.productSuppliers = response.data;
                    this.loading.main = false;
                })
                .catch(() => {
                    this.loading.main = false;
                });
        },
        getProductCategories() {
            this.loading.main = true;
            axios
                .get(route('product.categories'))
                .then((response) => {
                    this.productCategories = response.data;
                    this.getProduct();
                })
                .catch(() => {
                    this.loading.main = false;
                });
        },
    },
};
</script>
